import Logger from '../lib/logger';
import StorageService from '../lib/storage.service';
import { deepClone, wholeNumber } from '../lib/util.functions';

const DEFAULT_DATA = {
  type: 'custom',
  variantId: undefined,
  containerVariantId: undefined,
  containerVariantName: undefined,
  customText: undefined,
  customImage: undefined,
  price: 0,
  quantity: 0,
  title: undefined,
  image: undefined,
  sender: undefined,
  recipient: undefined,
  card: {
    variantId: undefined,
    title: undefined,
    price: 0,
    quantity: 0,
    message: undefined,
    signature: undefined,
    image: undefined
  },
  addedComponentReference: [],
  addedComponentQuantities: [],
  addedComponentPrices: [],
  addedComponentTitles: [],
  addedComponentImages: []
}

export default class CustomBundle {
  NAME = 'CustomBundle';
  #storageId = 'bundle-data';
  #dataObj = deepClone(DEFAULT_DATA);

  get type() {
    return this.#dataObj.type
  }

  get data() {
    return this.#dataObj
  }

  get hasContainer() {
    return !!this.#dataObj && !!this.#dataObj.variantId
  }

  get hasCard() {
    return !!this.#dataObj && !!this.#dataObj.card.variantId
  }

  get hasGifts() {
    return !!this.#dataObj && !!this.#dataObj.addedComponentReference.length
  }

  set sender(val) {
    this.#dataObj.sender = val;
    StorageService.save(this.#storageId, this.#dataObj, true);
  }
  get sender() {
    return this.#dataObj.sender;
  }

  set recipient(val) {
    this.#dataObj.recipient = val;
    StorageService.save(this.#storageId, this.#dataObj, true);
  }
  get recipient() {
    return this.#dataObj.recipient;
  }

  constructor(type) {
    const objFromStorage = StorageService.get(this.#storageId);
    if (type) {
      if (objFromStorage) this.remove();
      this.#dataObj.type = type;
    } else {
      if (objFromStorage) this.#dataObj = objFromStorage;
    }
    Logger.info(`${this.NAME} > constructor`);
    /* Logger.log(this.#dataObj); */
  }

  addVariant(id, quantity, price, title, image, containerVariantId, containerVariantName, customText, customImage) {
    this.#dataObj.variantId = id;
    this.#dataObj.price = wholeNumber(price);
    this.#dataObj.title = title;
    this.#dataObj.quantity = wholeNumber(quantity);
    this.#dataObj.image = image;
    this.#dataObj.containerVariantId = containerVariantId;
    this.#dataObj.containerVariantName = containerVariantName;
    this.#dataObj.customText = customText;
    this.#dataObj.customImage = customImage;
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  updateVariant({price, quantity, image, containerVariantId, containerVariantName, customText, customImage}) {
    if (quantity === 0) {
      this.remove();
    } else {
      if (price) this.#dataObj.price = wholeNumber(price);
      if (quantity) this.#dataObj.quantity = wholeNumber(quantity);
      if (image) this.#dataObj.image = image;
      if (containerVariantId) this.#dataObj.containerVariantId = containerVariantId;
      if (containerVariantName) this.#dataObj.containerVariantName = containerVariantName;
      if (customText) this.#dataObj.customText = customText;
      if (customImage) this.#dataObj.customImage = customImage;
    }
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  addCard(variantId, price, title, message, signature, image) {
    this.#dataObj.card = { 
      variantId,
      price: wholeNumber(price),
      title,
      quantity: 1,
      message, 
      signature,
      image
    };
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  updateCard({price, title, quantity, message, signature, image}) {
    if (quantity === 0) {
      this.#dataObj.card = {
        variantId: undefined,
        title: undefined,
        price: 0,
        quantity: 0,
        message: undefined,
        signature: undefined,
        image: undefined
      }
    } else {
      if (price) this.#dataObj.card.price = wholeNumber(price);
      if (title) this.#dataObj.card.title = title;
      if (message) this.#dataObj.card.message = message;
      if (signature) this.#dataObj.card.signature = signature;
      if (image) this.#dataObj.card.image = image;
      if (quantity) this.#dataObj.card.quantity = wholeNumber(quantity);
    }
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  addGift(variantId, quantity, price, title, image) {
    this.#dataObj.addedComponentReference.push(variantId);
    this.#dataObj.addedComponentQuantities.push(wholeNumber(quantity));
    this.#dataObj.addedComponentPrices.push(wholeNumber(price));
    this.#dataObj.addedComponentTitles.push(title);
    this.#dataObj.addedComponentImages.push(image);
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  updateGift(variantId, { quantity, price }) {
    const index = this.#dataObj.addedComponentReference.findIndex((id) => id === variantId);
    if (price) {
      this.#dataObj.addedComponentPrices[index] = wholeNumber(price);
    }
    if (quantity != undefined) {
      const quant = wholeNumber(quantity);
      if (quant === 0) {
        this.#dataObj.addedComponentReference.splice(index, 1);
        this.#dataObj.addedComponentQuantities.splice(index, 1);
        this.#dataObj.addedComponentPrices.splice(index, 1);
        this.#dataObj.addedComponentTitles.splice(index, 1);
        this.#dataObj.addedComponentImages.splice(index, 1);
      } else {
        this.#dataObj.addedComponentQuantities[index] = quant;
      }
    }
    StorageService.save(this.#storageId, this.#dataObj, true);
    return this.#dataObj;
  }

  remove() {
    StorageService.clear(this.#storageId);
    this.#dataObj = deepClone(DEFAULT_DATA);
    return this.#dataObj;
  }

  addToCartData() {
    if (!this.hasContainer) {
      throw new Error('Bundle is missing a container!');
    } else if (!this.hasGifts && this.#dataObj.type === 'custom') {
      throw new Error('Bundle has no gifts!');
    } else if (!this.hasCard) {
      throw new Error('Bundle is missing card!');
    }
    //
    let ids = [...this.#dataObj.addedComponentReference, this.#dataObj.card.variantId];
    let quantities = [...this.#dataObj.addedComponentQuantities, 1];
    let prices = [...this.#dataObj.addedComponentPrices, this.#dataObj.card.price];
    const hasContainerVariantId = !!this.#dataObj.containerVariantId;
    //
    if (hasContainerVariantId) {
      ids = [this.#dataObj.containerVariantId, ...this.#dataObj.addedComponentReference, this.#dataObj.card.variantId];
      quantities = [1, ...this.#dataObj.addedComponentQuantities, 1];
      prices = [0, ...this.#dataObj.addedComponentPrices, this.#dataObj.card.price];
    }
    //
    ids = ids.map(id => `gid://shopify/ProductVariant/${id}`);
    //
    const data = {
      'id': this.#dataObj.variantId,
      'properties': { 
        '_component_reference': JSON.stringify(ids),
        '_component_quantities': JSON.stringify(quantities),
        '_component_prices': JSON.stringify(prices),
        '_component_replace_container': JSON.stringify(hasContainerVariantId),
        'message': this.#dataObj.card.message,
        'signature': this.#dataObj.card.signature,
        'sender': this.#dataObj.sender,
        'recipient': this.#dataObj.recipient,
        '_title': this.#dataObj.title
      },
      "quantity": this.#dataObj.quantity.toString()
    };
    //
    if (this.#dataObj.customText) {
      data['properties']['text'] = this.#dataObj.customText;
    }
    //
    if (this.#dataObj.customImage) {
      data['properties']['image'] = this.#dataObj.customImage;
    }
    //
    return data;
  }

  getTotal() {
    const startingPrice = this.#dataObj.price + (this.#dataObj.card.price || 0)
    const arr = this.#dataObj.addedComponentPrices.map((p, i) => p * this.#dataObj.addedComponentQuantities[i]);
    return arr.reduce((a,b) => a + b, startingPrice) * this.#dataObj.quantity;
  }

  getNumItems() {
    return this.#dataObj.addedComponentQuantities.reduce((a,b) => a + b, this.#dataObj.quantity + this.#dataObj.card.quantity);
  }
}